import { useState, useEffect, useMemo } from 'react'
import { PageHeader } from '../components/UI'
import { get } from '../utils/api'
import { formatDate } from '../utils/helpers'
import { IconChevronLeft, IconChevronRight, IconPlus } from '@tabler/icons-react'

const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']

export default function CalendarPage() {
  const [view, setView] = useState('month')
  const [current, setCurrent] = useState(new Date())
  const [events, setEvents] = useState([])
  const [tasks, setTasks] = useState([])

  useEffect(() => { get('/events').then(setEvents).catch(()=>setEvents([])); get('/tasks').then(setTasks).catch(()=>setTasks([])) }, [])

  const year = current.getFullYear(), month = current.getMonth()
  const firstDay = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const today = new Date()

  const calendarDays = useMemo(() => {
    const days = []; for (let i = 0; i < firstDay; i++) days.push(null)
    for (let d = 1; d <= daysInMonth; d++) days.push(d); return days
  }, [year, month, firstDay, daysInMonth])

  const getEventsForDay = (day) => {
    if (!day) return []
    const ds = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`
    return [...events.filter(e => e.arrival_date <= ds && e.departure_date >= ds).map(e => ({...e,type:'event'})),
      ...tasks.filter(t => t.due_date === ds).map(t => ({...t,type:'task'}))]
  }
  const isToday = d => d && today.getFullYear()===year && today.getMonth()===month && today.getDate()===d

  return (
    <>
      <PageHeader title="Calendar" actions={<button className="btn btn-primary btn-sm"><IconPlus size={16} className="me-1" />New Event</button>} />
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-2">
          <button className="btn btn-ghost-secondary btn-icon btn-sm" onClick={()=>setCurrent(new Date(year,month-1,1))}><IconChevronLeft size={18} /></button>
          <h3 className="mb-0" style={{minWidth:180,textAlign:'center'}}>{current.toLocaleDateString('en-US',{month:'long',year:'numeric'})}</h3>
          <button className="btn btn-ghost-secondary btn-icon btn-sm" onClick={()=>setCurrent(new Date(year,month+1,1))}><IconChevronRight size={18} /></button>
          <button className="btn btn-outline-secondary btn-sm ms-2" onClick={()=>setCurrent(new Date())}>Today</button>
        </div>
        <div className="btn-group">
          {['month','week','agenda'].map(v => (
            <button key={v} className={`btn btn-sm ${view===v?'btn-primary':'btn-outline-secondary'}`} onClick={()=>setView(v)}>{v.charAt(0).toUpperCase()+v.slice(1)}</button>
          ))}
        </div>
      </div>
      {view === 'agenda' ? (
        <div className="card"><div className="list-group list-group-flush">
          {[...events.map(e=>({title:e.event_name,date:e.arrival_date,detail:`${e.company} - ${e.room_count} rooms`,color:'primary'})),
            ...tasks.filter(t=>t.status==='pending').map(t=>({title:t.title,date:t.due_date,detail:t.related_to,color:t.priority==='high'?'danger':'warning'}))
          ].sort((a,b)=>a.date.localeCompare(b.date)).map((item,i)=>(
            <div key={i} className="list-group-item">
              <div className="row align-items-center">
                <div className="col-auto"><span className={`status-dot bg-${item.color}`}/></div>
                <div className="col"><div className="fw-medium">{item.title}</div><div className="text-secondary" style={{fontSize:'0.8rem'}}>{formatDate(item.date,{month:'short',day:'numeric'})} — {item.detail}</div></div>
              </div>
            </div>))}
        </div></div>
      ) : (
        <div className="card">
          <div className="calendar-grid border-bottom">
            {DAYS.map(d => <div key={d} className="text-center py-2 fw-bold text-secondary" style={{fontSize:'0.8rem'}}>{d}</div>)}
          </div>
          <div className="calendar-grid">
            {calendarDays.map((day, i) => {
              const dayEvents = getEventsForDay(day)
              return (
                <div key={i} className="calendar-cell" style={{background: !day ? 'var(--tblr-bg-surface-secondary)' : undefined}}>
                  {day && (<>
                    <span className={`d-inline-flex align-items-center justify-content-center rounded-circle ${isToday(day)?'bg-primary text-white':''}`} style={{width:24,height:24,fontSize:'0.8rem',fontWeight:500}}>{day}</span>
                    <div className="mt-1">{dayEvents.slice(0,3).map((ev,j) => (
                      <div key={j} className={`badge ${ev.type==='event'?'bg-primary-lt':'bg-warning-lt'} d-block text-truncate mb-1`} style={{fontSize:'0.65rem'}}>{ev.type==='event'?ev.event_name:ev.title}</div>
                    ))}{dayEvents.length>3&&<div style={{fontSize:'0.65rem'}} className="text-secondary">+{dayEvents.length-3} more</div>}</div>
                  </>)}
                </div>)
            })}
          </div>
        </div>
      )}
    </>
  )
}
